/**
 * Google Pay "Add to Wallet" entry modal.
 *
 * Collects last4 (and billing, when required) on the brand's own domain,
 * validates the card, then hands off to the shared provisioning subdomain (see
 * PROVISIONING_SUBDOMAIN_PLAN.md at the repo root) for the actual Google Pay
 * SDK popup — that subdomain is the only origin ever registered with Google.
 *
 * Flow:
 *   Fingerprint is prefetched as soon as the card token is known (see the
 *   effect in giftcard-home.js), well before this modal is ever opened —
 *   giving several seconds between fingerprint creation and its first use.
 *
 *   Continue button click:
 *     Screen 1: validate last4
 *     Screen 2 (when cardConfig.googlePaySendBilling): collect + validate billing
 *     fingerprint  → reused from sessionStorage (prefetched); fetched here
 *                    as a fallback via ensureFingerprintId() if it wasn't
 *     validateCard → POST /api/v2/token/{token}
 *     createWalletProvisioningSession → POST /api/wallet/provisioning-session
 *     window.location.assign(provisioningUrl)  — full top-level redirect
 *
 * The brand page never sees onSuccess/onFailure/onCancel directly — those
 * happen on the shared page, which redirects back here with
 * ?walletResult=success|failure|cancelled (handled by the parent, CardFunctions).
 *
 * Props:
 *   token      {string}   9-digit card token
 *   cardConfig {object}   brand cardConfig from /api/clientConfig
 *                         cardConfig.googlePaySendBilling {bool} — include billing in request
 *   onClose    {function} called when modal is dismissed
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../api';
import { ensureFingerprintId } from '../utils/fingerprintManager';
import { GOOGLE_SERVICE_COUNTRIES } from '../utils/constants';
import './google-pay-provisioning.css';

const STEPS = {
    INPUT:      'input',
    BILLING:    'billing',     // billing address screen (only when sendBilling)
    SUBMITTING: 'submitting',  // validate + session create, before redirect
    ERROR:      'error',
};

const CountrySelect = ({ id, value, onChange, options, placeholder }) => {
    const [open, setOpen] = useState(false);
    const selected = options.find((c) => c.code === value);
    const SelectedFlag = selected?.Flag;

    const searchRef = useRef('');
    const searchTimeoutRef = useRef(null);
    const optionRefs = useRef([]);
    const containerRef = useRef(null);

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, []);

    // Close the dropdown when the user clicks anywhere outside the component.
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const resetSearch = useCallback(() => {
        searchRef.current = '';
    }, []);

    const handleSelect = (code) => {
        onChange(code);
        setOpen(false);
        searchRef.current = '';
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };

    const handleKeyDown = (e) => {
        if (!open) return;
        if (e.key.length === 1 && /[a-zA-Z0-9\s]/.test(e.key)) {
            e.preventDefault();
            searchRef.current += e.key.toLowerCase();
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(resetSearch, 500);

            const matchIndex = options.findIndex((country) =>
                country.name.toLowerCase().startsWith(searchRef.current)
            );
            const matchEl = optionRefs.current[matchIndex];
            if (matchEl) matchEl.scrollIntoView({ block: 'nearest' });
        }
    };

    return (
        <div className="googlePayModal__countrySelect" ref={containerRef}>
            <button
                id={id}
                type="button"
                className="googlePayModal__countrySelect-trigger"
                onClick={() => setOpen((prev) => !prev)}
                onKeyDown={handleKeyDown}
            >
                {selected ? (
                    <>
                        <SelectedFlag className="googlePayModal__countrySelect-flag" aria-hidden="true" />
                        <span>{selected.name}</span>
                    </>
                ) : (
                    <span>{placeholder}</span>
                )}
                <span>▼</span>
            </button>
            {open && (
                <ul className="googlePayModal__countrySelect-options googlePayModal__countrySelect-options--upward">
                    {options.map((country, idx) => {
                        const CountryFlag = country.Flag;
                        return (
                            <li
                                key={country.code}
                                ref={(el) => { optionRefs.current[idx] = el; }}
                                className="googlePayModal__countrySelect-option"
                                onClick={() => handleSelect(country.code)}
                            >
                                <CountryFlag className="googlePayModal__countrySelect-flag" aria-hidden="true" />
                                <span>{country.name}</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

/**
 * Build billing object for the wallet request.
 * Returns undefined when billing is disabled.
 * Optional fields (line2) are omitted when empty.
 */
function buildBilling(sendBilling, address) {
    if (!sendBilling || !address) return undefined;
    const obj = {
        line1:      address.line1      || '',
        city:       address.city       || '',
        postalCode: address.postalCode || '',
        country:    address.country    || '',
    };
    if (address.line2?.trim()) obj.line2 = address.line2.trim();
    return obj;
}

// ISO 3166-1 alpha-3 code for the United Kingdom, as used by GOOGLE_SERVICE_COUNTRIES.
const UK_COUNTRY_CODE = 'GBR';

const EMPTY_BILLING = {
    line1: '', line2: '', city: '', postalCode: '', country: '',
};

/**
 * Send a telemetry event to the backend. Never throws, and no ticket is
 * available yet at this stage of the flow.
 */
const sendTelemetry = (api, ticket, event, payload) => {
    if (!api?.reportWalletProvisioningLog) return;
    api.reportWalletProvisioningLog(ticket, event, payload).catch(() => {});
};

const GooglePayProvisioningModal = ({ token, cardConfig = {}, onClose }) => {
    const { t } = useTranslation();
    const api = API();

    const [step, setStep] = useState(STEPS.INPUT);
    const [last4, setLast4] = useState('');
    const [last4Error, setLast4Error] = useState('');
    const [billing, setBilling] = useState(EMPTY_BILLING);
    const [billingErrors, setBillingErrors] = useState({});
    const [postcodeLookup, setPostcodeLookup] = useState({ addresses: [], selectedIndex: -1, loading: false, error: '' });
    const [showAddressPicker, setShowAddressPicker] = useState(false);
    // True once a lookup result has filled Address line 1, Line 2 and City.
    // Those fields are then read-only until the postal code is looked up again.
    const [addressAutoFilled, setAddressAutoFilled] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const dialogRef = useRef(null);

    const sendBilling = !!cardConfig.googlePaySendBilling;

    // The UK uses postcode lookup. Any other country gets the plain form.
    const isUK = billing.country === UK_COUNTRY_CODE;
    // Lock Line 1, Line 2 and City only after a lookup result has filled them.
    const lockAutoFilled = isUK && addressAutoFilled;

    // Used to tell "Required" validation messages apart from other error
    // messages so only "Required" can be styled with the success colour.
    const requiredMessage = t('googlePay.required', 'Required');

    useEffect(() => {
        if (dialogRef.current && !dialogRef.current.open) dialogRef.current.showModal();
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    const handleClose = useCallback(() => {
        if (dialogRef.current && dialogRef.current.open) dialogRef.current.close();
        onClose();
    }, [onClose]);

    // Only the × button (and the Cancel button in the error step) may close the
    // modal. Backdrop clicks are ignored, and Escape is blocked so the native
    // <dialog> does not close without going through handleClose.
    const handleDialogCancel = (e) => {
        e.preventDefault();
    };

    const isLast4Valid = (val) => /^\d{4}$/.test(val);

    const handleLast4Change = (e) => {
        const val = e.target.value.replace(/\D/g, '').slice(0, 4);
        setLast4(val);
        setLast4Error('');
    };

    const handleBillingChange = (e) => {
        const { name, value } = e.target;
        setBilling(prev => ({ ...prev, [name]: value }));
        setBillingErrors(prev => ({ ...prev, [name]: '' }));
    };

    // Changing country resets the UK lookup state, so a previous lookup
    // result does not leave fields locked for a different country.
    const handleCountryChange = (code) => {
        handleBillingChange({ target: { name: 'country', value: code } });
        setAddressAutoFilled(false);
        setShowAddressPicker(false);
        setPostcodeLookup({ addresses: [], selectedIndex: -1, loading: false, error: '' });
    };

    // Joins the address parts into one readable line for the results list,
    // e.g. "1 Owen Close, Fareham, Hampshire".
    // The API repeats the business name at the start of formatted_address
    // (e.g. "Harlees Fish & Chips, Harlees Fish & Chips, 5-7 High Street"),
    // so drop consecutive duplicate parts.
    const formatAddressLabel = (addr) => {
        const parts = (addr.formatted_address && addr.formatted_address.length)
            ? addr.formatted_address
            : [addr.line_1, addr.line_2, addr.town_or_city, addr.county];
        return parts.filter(Boolean).filter((part, i, arr) => part !== arr[i - 1]).join(', ');
    };

    // Looks up addresses for the postal code and lists them for selection.
    // Nothing is filled in until the user picks one.
    const handleFindAddress = async () => {
        const postcode = billing.postalCode.trim();
        if (!postcode) {
            setPostcodeLookup(prev => ({ ...prev, error: t('googlePay.postcodeRequired', 'Enter a postal code') }));
            return;
        }
        setPostcodeLookup({ addresses: [], selectedIndex: -1, loading: true, error: '' });
        setAddressAutoFilled(false);
        try {
            const result = await api.addressLookup(postcode);
            const addresses = (result && result.addresses) || [];
            if (addresses.length === 0) {
                setPostcodeLookup({ addresses: [], selectedIndex: -1, loading: false, error: t('googlePay.noAddresses', 'No addresses found for this postcode') });
                return;
            }
            setPostcodeLookup({ addresses, selectedIndex: -1, loading: false, error: '' });
            setShowAddressPicker(true);
        } catch (err) {
            setPostcodeLookup({ addresses: [], selectedIndex: -1, loading: false, error: t('googlePay.lookupFailed', 'Address lookup failed. Enter your address manually.') });
        }
    };

    // Fills Address line 1, Address line 2 and City from the chosen result.
    const handleSelectAddress = (idx) => {
        const addr = postcodeLookup.addresses[idx];
        if (!addr) return;
        setBilling(prev => ({
            ...prev,
            line1: addr.line_1 || addr.formatted_address?.[0] || '',
            line2: addr.line_2 || '',
            city: addr.town_or_city || '',
        }));
        setBillingErrors(prev => ({ ...prev, line1: '', city: '' }));
        setPostcodeLookup(prev => ({ ...prev, selectedIndex: idx }));
        setAddressAutoFilled(true);
        setShowAddressPicker(false);
    };

    const validateBilling = () => {
        if (!sendBilling) return true;
        const errors = {};
        if (!billing.line1.trim())      errors.line1      = t('googlePay.required', 'Required');
        if (!billing.city.trim())       errors.city       = t('googlePay.required', 'Required');
        if (!billing.postalCode.trim()) errors.postalCode = t('googlePay.required', 'Required');
        if (!billing.country.trim())    errors.country    = t('googlePay.required', 'Required');
        setBillingErrors(errors);
        return Object.keys(errors).length === 0;
    };

    /**
     * handleProvision — called from the Continue button click.
     * Validates the card, creates a provisioning session, then redirects to
     * the shared provisioning subdomain for the actual Google Pay SDK popup.
     */
    const validateLast4 = () => {
        if (!isLast4Valid(last4)) {
            setLast4Error(t('googlePay.last4Invalid', 'Enter exactly 4 digits'));
            return false;
        }
        return true;
    };

    /**
     * handleContinue — advances through the multi-step entry flow.
     *   INPUT  → validates last4, then either BILLING (if sendBilling) or provision.
     *   BILLING → validates billing, then provision.
     */
    const handleContinue = () => {
        if (step === STEPS.INPUT) {
            if (!validateLast4()) return;
            if (sendBilling) {
                setStep(STEPS.BILLING);
                return;
            }
        } else if (step === STEPS.BILLING) {
            if (!validateBilling()) return;
        }
        handleProvision();
    };

    const handleProvision = async () => {
        const billingSnapshot = sendBilling ? { ...billing } : null;

        setStep(STEPS.SUBMITTING);
        sendTelemetry(api, null, 'provision_initiated', {
            sendBilling,
            userAgent: navigator.userAgent,
        });

        try {
            const fingerprintId = await ensureFingerprintId(api);

            const cardResponse = await api.validateCardV2(token, last4, fingerprintId);
            if (cardResponse.error) throw new Error(cardResponse.error);
            const cardId = cardResponse.cardId || cardResponse.card_id;
            if (!cardId) throw new Error('Card validation failed: no cardId returned');
            sendTelemetry(api, null, 'card_validated', { cardId });

            const sessionResponse = await api.createWalletProvisioningSession({
                provider: 'google',
                cardId,
                fingerprintId,
                billing: buildBilling(sendBilling, billingSnapshot),
                returnPath: window.location.pathname + window.location.hash,
            });
            if (sessionResponse.error || !sessionResponse.provisioningUrl) {
                throw new Error(sessionResponse.error || 'Could not start Google Pay provisioning');
            }
            sendTelemetry(api, null, 'session_created', {
                cardId,
                ticket: sessionResponse.ticket,
            });

            window.location.assign(sessionResponse.provisioningUrl);

        } catch (err) {
            console.error('Google Pay provision error:', err);
            sendTelemetry(api, null, 'provision_error', { message: err.message });
            setErrorMsg(err.message || t('googlePay.genericError', 'Something went wrong. Please try again.'));
            setStep(STEPS.ERROR);
        }
    };

    const handleRetry = () => {
        setErrorMsg('');
        setStep(sendBilling ? STEPS.BILLING : STEPS.INPUT);
    };

    return (
        <dialog
            ref={dialogRef}
            className="googlePayModal"
            onCancel={handleDialogCancel}
            aria-modal="true"
            aria-labelledby="gpay-modal-title"
        >
            <div className="googlePayModal__container">
                <div className="googlePayModal__content">
                    <button
                        className="googlePayModal__close"
                        onClick={handleClose}
                        aria-label={t('googlePay.close', 'Close')}
                    >×</button>

                    <div className="googlePayModal__main">
                        <div className="googlePayModal__logo">
                            <img
                                className="googlePayModal__logo-image"
                                src="/assets/google-wallet/GoogleWalletLogo.svg"
                                alt={t('googlePay.logoAlt', 'Google Wallet')}
                            />
                        </div>

                    <h2 id="gpay-modal-title" className="googlePayModal__title">
                        {t('googlePay.title', 'Add your Gift Card to your Google Wallet')}
                    </h2>

                    {step === STEPS.INPUT && (
                        <div className="googlePayModal__step googlePayModal__step--input">
                            <p>{t('googlePay.last4Prompt', 'Enter the last 4 digits of your card to continue.')}</p>

                            <label htmlFor="gpay-last4">{t('googlePay.last4Label', 'Last 4 digits')}</label>
                            <input
                                id="gpay-last4"
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                value={last4}
                                onChange={handleLast4Change}
                                placeholder="0000"
                                autoFocus
                            />
                            {last4Error && <span className="error">{last4Error}</span>}

                            <button
                                className={`button googlePayModal__cta disabled-${!isLast4Valid(last4)}`}
                                onClick={handleContinue}
                                disabled={!isLast4Valid(last4)}
                            >
                                {t('googlePay.continue', 'Continue')}
                            </button>
                        </div>
                    )}

                    {step === STEPS.BILLING && (
                        <div className="googlePayModal__step googlePayModal__step--billing">
                            <div className="googlePayModal__billing">
                                <h3>{t('googlePay.billingTitle', 'Billing address')}</h3>

                                <label htmlFor="gpay-country">{t('googlePay.country', 'Country')} *</label>
                                <CountrySelect
                                    id="gpay-country"
                                    value={billing.country}
                                    onChange={handleCountryChange}
                                    options={GOOGLE_SERVICE_COUNTRIES}
                                    placeholder={t('googlePay.selectCountry', 'Select a country')}
                                />
                                {billingErrors.country && (
                                    <span className={`error${billingErrors.country === requiredMessage ? ' error--required' : ''}`}>
                                        {billingErrors.country}
                                    </span>
                                )}

                                {billing.country && (
                                    <>
                                        {isUK ? (
                                            <>
                                                <label htmlFor="gpay-postal">{t('googlePay.postalCode', 'Postal code')} *</label>
                                                <div className="googlePayModal__lookup">
                                                    <input id="gpay-postal" name="postalCode" type="text"
                                                        value={billing.postalCode} onChange={handleBillingChange} />
                                                    <button type="button" className="button googlePayModal__lookupBtn"
                                                        onClick={handleFindAddress} disabled={postcodeLookup.loading}>
                                                        {postcodeLookup.loading ? t('googlePay.finding', 'Finding…') : t('googlePay.find', 'Find')}
                                                    </button>
                                                </div>
                                                {billingErrors.postalCode && (
                                                    <span className={`error${billingErrors.postalCode === requiredMessage ? ' error--required' : ''}`}>
                                                        {billingErrors.postalCode}
                                                    </span>
                                                )}
                                                {postcodeLookup.error && <span className="error">{postcodeLookup.error}</span>}

                                                {showAddressPicker && postcodeLookup.addresses.length > 0 && (
                                                    <div className="googlePayModal__addressPopup" role="dialog" aria-modal="true"
                                                        aria-label={t('googlePay.selectAddress', 'Select an address')}>
                                                        <div className="googlePayModal__addressPopupCard">
                                                            <div className="googlePayModal__addressPopupHead">
                                                                <span className="googlePayModal__addressListLabel">
                                                                    {t('googlePay.selectAddress', 'Select an address')}
                                                                </span>
                                                            </div>
                                                            <ul className="googlePayModal__addressOptions" role="listbox">
                                                                {postcodeLookup.addresses.map((addr, idx) => (
                                                                    <li key={idx} role="option" aria-selected={postcodeLookup.selectedIndex === idx}>
                                                                        <button type="button"
                                                                            className={`googlePayModal__addressOption${postcodeLookup.selectedIndex === idx ? ' is-selected' : ''}`}
                                                                            onClick={() => handleSelectAddress(idx)}>
                                                                            {formatAddressLabel(addr)}
                                                                        </button>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <label htmlFor="gpay-postal">{t('googlePay.postalCode', 'Postal code')} *</label>
                                                <input id="gpay-postal" name="postalCode" type="text"
                                                    value={billing.postalCode} onChange={handleBillingChange} />
                                                {billingErrors.postalCode && (
                                                    <span className={`error${billingErrors.postalCode === requiredMessage ? ' error--required' : ''}`}>
                                                        {billingErrors.postalCode}
                                                    </span>
                                                )}
                                            </>
                                        )}

                                        <label htmlFor="gpay-line1">{t('googlePay.line1', 'Address line 1')} *</label>
                                        <input id="gpay-line1" name="line1" type="text"
                                            className={lockAutoFilled ? 'is-locked' : undefined}
                                            value={billing.line1} onChange={handleBillingChange}
                                            readOnly={lockAutoFilled} aria-readonly={lockAutoFilled} />
                                        {billingErrors.line1 && (
                                            <span className={`error${billingErrors.line1 === requiredMessage ? ' error--required' : ''}`}>
                                                {billingErrors.line1}
                                            </span>
                                        )}

                                        <label htmlFor="gpay-line2">{t('googlePay.line2', 'Address line 2')}</label>
                                        <input id="gpay-line2" name="line2" type="text"
                                            className={lockAutoFilled ? 'is-locked' : undefined}
                                            value={billing.line2} onChange={handleBillingChange}
                                            readOnly={lockAutoFilled} aria-readonly={lockAutoFilled} />

                                        <label htmlFor="gpay-city">{t('googlePay.city', 'City')} *</label>
                                        <input id="gpay-city" name="city" type="text"
                                            className={lockAutoFilled ? 'is-locked' : undefined}
                                            value={billing.city} onChange={handleBillingChange}
                                            readOnly={lockAutoFilled} aria-readonly={lockAutoFilled} />
                                        {billingErrors.city && (
                                            <span className={`error${billingErrors.city === requiredMessage ? ' error--required' : ''}`}>
                                                {billingErrors.city}
                                            </span>
                                        )}
                                    </>
                                )}
                            </div>

                            <button
                                className="button googlePayModal__cta"
                                onClick={handleContinue}
                            >
                                {t('googlePay.continue', 'Continue')}
                            </button>
                        </div>
                    )}

                    {step === STEPS.SUBMITTING && (
                        <div className="googlePayModal__step googlePayModal__step--loading">
                            <p>{t('googlePay.stepOpc', 'Requesting payment credentials…')}</p>
                        </div>
                    )}

                    {step === STEPS.ERROR && (
                        <div className="googlePayModal__step googlePayModal__step--error">
                            <p className="error">{errorMsg}</p>
                            <button className="button" onClick={handleRetry}>
                                {t('googlePay.retry', 'Try again')}
                            </button>
                            <button className="button button--secondary" onClick={handleClose}>
                                {t('googlePay.cancel', 'Cancel')}
                            </button>
                        </div>
                    )}
                </div>
                </div>

                <div className="googlePayModal__notice">
                    {t('googlePay.popupNotice', 'Pop-ups are required to continue with Google Pay. If disabled, please enable them on your browser.')}
                </div>
            </div>
        </dialog>
    );
};

export default GooglePayProvisioningModal;
